import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "html-css",
  name: "HTML/CSS",
  nameEn: "HTML & CSS",
  seo: {
    title: "HTML/CSSを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "HTML/CSSはすべてのWebエンジニアの必須スキル。プログラミングの第一歩として最適です。ゆめさくエンジニアで基礎から実践まで学びましょう。",
    url: "/columns/html-css-programming-school",
  },
  hero: {
    title: "HTML/CSSを学べる女性向けプログラミングスクール",
    englishTitle: "Learn HTML & CSS",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "HTML/CSS" },
  ],
  overview: {
    heading: "HTML/CSSとは？",
    paragraphs: [
      "HTMLはWebページの構造を定義するマークアップ言語、CSSはWebページの見た目（色・レイアウト・フォントなど）を指定するスタイルシート言語です。この2つはWebサイト制作の土台であり、すべてのWebエンジニアが最初に学ぶスキルです。",
      "プログラミング言語と比べてルールがシンプルで、書いたコードがすぐにブラウザで目に見える形で表示されるため、プログラミングの入口として最も始めやすい技術です。",
      "近年はCSS Grid・Flexbox・レスポンシブデザインなど、CSSだけでも高度なレイアウトが実現できるようになっており、スキルの深さも十分にあります。",
    ],
    useCases: [
      {
        label: "Webサイト制作",
        description:
          "企業のコーポレートサイト、ランディングページ、ブログなど、あらゆるWebサイトの基礎となります。",
      },
      {
        label: "レスポンシブデザイン",
        description:
          "PC・タブレット・スマートフォンなど、画面サイズに応じて最適なレイアウトに自動調整するWebページを作れます。",
      },
      {
        label: "メールテンプレート作成",
        description:
          "HTMLメールのデザインやテンプレート作成は、マーケティング職でも需要の高いスキルです。",
      },
      {
        label: "Webアプリの画面実装",
        description:
          "ReactやVueなどのフレームワークでもHTML/CSSの知識は必須。UI構築の基盤を支えます。",
      },
    ],
  },
  whyLearn: {
    heading: "HTML/CSSを学ぶべき3つの理由",
    items: [
      {
        title: "すべてのWebスキルの基礎になる",
        description:
          "JavaScript、React、WordPressなど、Web系の技術はすべてHTML/CSSの上に成り立っています。ここを確実に理解しておくことで、後の学習がスムーズに進みます。",
      },
      {
        title: "目に見える成果がすぐ出る",
        description:
          "コードを書くとブラウザにすぐ反映されるため、学習のモチベーションを維持しやすいのが大きな特徴です。「自分の手でWebページを作った」という成功体験が、プログラミング学習の原動力になります。",
      },
      {
        title: "エンジニア以外の職種でも役立つ",
        description:
          "Webデザイナー、マーケター、ディレクターなど、エンジニア以外の職種でもHTML/CSSの知識は重宝されます。Webサイトの修正やメールテンプレートの作成など、実務で活きる場面が多数あります。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでHTML/CSSを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアのカリキュラムはHTML/CSSからスタートします。最初の数週間で基本的なWebページを作成し、レスポンシブ対応やモダンなCSS設計を段階的に学んでいきます。",
      "実際のWebサイトのデザインカンプからコーディングする実践的なプロジェクトを通じて、現場で即戦力となるスキルを身につけます。",
    ],
    topics: [
      "HTMLの基本構造・セマンティックHTML",
      "CSSの基本（セレクタ・プロパティ・ボックスモデル）",
      "Flexbox・CSS Gridによるレイアウト",
      "レスポンシブデザイン・メディアクエリ",
      "CSS変数・BEM設計",
      "実践プロジェクト：レスポンシブWebサイト制作",
    ],
  },
  careerPaths: {
    heading: "HTML/CSSを活かせるキャリア",
    items: [
      {
        title: "Web制作エンジニア（コーダー）",
        description:
          "デザインカンプを正確にHTML/CSSでコーディングする職種です。フリーランスとして独立しやすく、リモートワークの案件も豊富にあります。",
        salary: "350〜500万円",
      },
      {
        title: "フロントエンドエンジニア",
        description:
          "HTML/CSSに加えてJavaScript・Reactなどを組み合わせ、より高度なWebアプリケーションの画面を構築します。Web制作から一段上のキャリアを目指せます。",
        salary: "450〜750万円",
      },
      {
        title: "Webデザイナー",
        description:
          "デザインツールでの画面設計とHTML/CSSによるコーディングの両方ができるデザイナーは、市場価値が高く重宝されます。",
        salary: "350〜550万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "2〜4ヶ月（基礎のみ）",
        luagate: "2〜3週間で基礎完了、実践へ",
      },
      {
        label: "挫折率",
        selfStudy: "CSSのレイアウト崩れで挫折する人が多い",
        luagate: "Flexbox/Gridの仕組みを図解で丁寧に指導",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "見本通りのコピーに終わりがち",
        luagate: "デザインカンプから自力でコーディング",
      },
      {
        label: "質問対応",
        selfStudy: "レイアウト崩れの原因がわからない",
        luagate: "メンターがDevToolsの使い方から指導",
      },
      {
        label: "就職サポート",
        selfStudy: "HTML/CSSだけでの就職は難しい",
        luagate: "JS・Reactまでセットで学び、就職力を確保",
      },
    ],
  },
  faq: [
    {
      question: "HTML/CSSはプログラミング言語ですか？",
      answer:
        "厳密にはHTMLはマークアップ言語、CSSはスタイルシート言語であり、プログラミング言語とは異なります。しかし、Web開発の基礎として必須のスキルであり、プログラミング学習の最初のステップとして最適です。",
    },
    {
      question: "HTML/CSSだけでWebエンジニアになれますか？",
      answer:
        "HTML/CSSだけでもWeb制作の仕事は可能ですが、JavaScriptやReactなども組み合わせることで活躍の幅が大きく広がります。ゆめさくエンジニアではHTML/CSSを起点に、段階的にスキルを積み上げるカリキュラムを提供しています。",
    },
    {
      question: "HTML/CSSの学習にどれくらい時間がかかりますか？",
      answer:
        "基本的なWebページを作れるようになるまで2〜4週間、レスポンシブ対応など実務レベルのスキルを身につけるには1〜2ヶ月が目安です。ゆめさくエンジニアでは効率的に学べるため、さらに短期間で習得できます。",
    },
    {
      question: "CSSが難しいと聞きますが本当ですか？",
      answer:
        "CSSは基礎は簡単ですが、思い通りのレイアウトを実現するには経験が必要です。特にFlexbox・Gridの使い分けやレスポンシブ対応は練習が重要です。ゆめさくエンジニアでは実践を通じて感覚を掴めるよう指導しています。",
    },
  ],
  relatedLanguages: [
    { slug: "javascript", name: "JavaScript" },
    { slug: "react", name: "React" },
    { slug: "typescript", name: "TypeScript" },
    { slug: "python", name: "Python" },
  ],
};
